import CustomButton from '@/common/CustomButton';
import Layout from '@/common/Layout';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPlanAnalytics, publishPlan, selectTaskSlice } from '../redux/slice';
import {
  ArrowLeftOutlined,
  LoadingOutlined,
  SendOutlined,
  UserOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faHourglassHalf,
  faRotateRight,
  faSitemap,
  faUserCheck
} from '@fortawesome/free-solid-svg-icons';
import { faUser } from '@fortawesome/free-regular-svg-icons';
import { Avatar, Spin, Tag } from 'antd';
import ColorHash from 'color-hash';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import TaskCard from '../components/TaskCard';

const colorHash = new ColorHash({ lightness: 0.7 });

const analyticsInitData = {
  unassignedClients: [],
  unassignedClientsCounts: 0,
  availableTeamMembers: [],
  availableTeamMembersCounts: 0,
  unassignedTasks: [],
  unassignedTasksCounts: 0,
  freqReleasedTasks: [],
  freqReleasedTasksCounts: 0
};

export default function PublishPlan() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { loading, planAnalyticsLoading } = useSelector(selectTaskSlice);
  const [analyticsData, setAnalyticsData] = useState(analyticsInitData);

  const handleGetPlanAnalytics = () => {
    const payload = {
      plan_date: state.plan_date
    };
    dispatch(getPlanAnalytics(payload)).then((res) => {
      if (res?.payload?.success) {
        const data = res.payload;
        setAnalyticsData({
          unassignedClients: data.unassigned_clients,
          unassignedClientsCounts: data.unassigned_clients_count,
          availableTeamMembers: data.unassigned_users,
          availableTeamMembersCounts: data.unassigned_users_count,
          unassignedTasks: data.unassigned_tasks,
          unassignedTasksCounts: data.unassigned_tasks_count,
          freqReleasedTasks: data.released_tasks,
          freqReleasedTasksCounts: data.released_tasks_count
        });
      }
    });
  };

  useEffect(() => {
    if (state.plan_date) {
      handleGetPlanAnalytics();
    } else {
      navigate('/tasks');
    }
  }, []);

  const handlePublish = () => {
    const payload = {
      is_published: true,
      plan_date: state.plan_date
    };
    dispatch(publishPlan(payload)).then((res) => {
      if (res?.payload?.success) {
        navigate('/dashboard', { replace: true });
      }
    });
  };

  return (
    <Layout sider={false}>
      <div className="flex flex-col gap-6 pb-6">
        {/* header */}
        <div className="flex flex-col gap-1">
          <label className={'!text-xl md:!text-3xl !font-bold md:mt-0 mt-2'}>
            Plan Analytics / {state.plan_date}
          </label>
        </div>

        {planAnalyticsLoading && _.isEqual(analyticsData, analyticsInitData) ? (
          <Spin
            size="large"
            indicator={<LoadingOutlined spin />}
            className="fixed -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2"
          />
        ) : (
          <div className="flex flex-col gap-6">
            {/* unassgined clients */}
            <div className="flex flex-col gap-3">
              <label className="font-semibold">
                <FontAwesomeIcon
                  icon={faSitemap}
                  className="mr-1.5 opacity-50"
                />
                Unassigned Clients{' '}
                {analyticsData.unassignedClientsCounts > 0
                  ? `(${analyticsData.unassignedClientsCounts})`
                  : null}
              </label>
              <div className="flex flex-wrap gap-2">
                {analyticsData.unassignedClients.map((client) => {
                  return (
                    <Tag
                      key={client.id}
                      icon={
                        <FontAwesomeIcon
                          icon={faBuilding}
                          className="mr-1.5 opacity-50"
                        />
                      }
                      className="!bg-orange-50 !text-orange-700 px-2 py-1 !font-semibold"
                      style={{ border: '1px solid #fed7aa' }}
                    >
                      {client.client_name}
                    </Tag>
                  );
                })}
              </div>
            </div>
            {/* available team members */}
            <div className="flex flex-col gap-3">
              <label className="font-semibold">
                <FontAwesomeIcon icon={faUser} className="mr-1.5 opacity-50" />
                Available Team Members{' '}
                {analyticsData.availableTeamMembersCounts > 0
                  ? `(${analyticsData.availableTeamMembersCounts})`
                  : null}
              </label>
              <div className="flex flex-wrap gap-2">
                {analyticsData.availableTeamMembers.map((user) => {
                  const initials =
                    String(user.first_name).charAt(0) +
                    String(user.last_name).charAt(0);
                  return (
                    <div
                      key={user.id}
                      className="flex items-center justify-between gap-2 px-4 py-2 bg-white rounded-lg whitespace-nowrap"
                      style={{ border: '1px dashed #e5e7eb' }}
                    >
                      <Avatar
                        style={{
                          backgroundColor: colorHash.hex(initials)
                        }}
                        size="small"
                      >
                        {initials}
                      </Avatar>
                      <label className="!text-xs font-semibold text-gray-600">
                        {`${user.first_name} ${user.last_name}`}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* unassgined tasks */}
            <div className="flex flex-col gap-3">
              <label className="font-semibold">
                <FontAwesomeIcon
                  icon={faHourglassHalf}
                  className="mr-1.5 opacity-50"
                />
                Persistent Unassigned Tasks{' '}
                {analyticsData.unassignedTasksCounts > 0
                  ? `(${analyticsData.unassignedTasksCounts})`
                  : null}
              </label>
              <div className="flex flex-col gap-4">
                {analyticsData.unassignedTasks.map((task) => {
                  return (
                    <TaskCard
                      key={task.id}
                      data={task}
                      type="unassigned_tasks"
                    />
                  );
                })}
              </div>
            </div>

            {/* frequently released tasks */}
            <div className="flex flex-col gap-3">
              <label className="font-semibold">
                <FontAwesomeIcon
                  icon={faRotateRight}
                  className="mr-1.5 opacity-50"
                />
                Frequently Released Tasks{' '}
                {analyticsData.freqReleasedTasksCounts > 0
                  ? `(${analyticsData.freqReleasedTasksCounts})`
                  : null}
              </label>
              <div className="flex flex-col gap-4 ">
                {analyticsData.freqReleasedTasks.map((task) => {
                  return (
                    <TaskCard key={task.id} data={task} type="released_tasks" />
                  );
                })}
              </div>
            </div>

            {/* action button */}
            <div
              className="flex items-center justify-between gap-2 pt-6"
              style={{ borderTop: '1px solid #e5e7eb' }}
            >
              <CustomButton
                type="outlined"
                onClick={() => navigate('/tasks')}
                className={'!bg-white'}
              >
                <ArrowLeftOutlined /> Return to Planning
              </CustomButton>
              <CustomButton
                type="primary"
                onClick={handlePublish}
                loading={loading}
              >
                <SendOutlined /> Publish Plan
              </CustomButton>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
